import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users, LayoutGrid, Store, Gavel, Layers, Cpu,
  ChevronRight, Activity, Sparkles, Ticket, Cog,
} from "lucide-react";
import { useBranding } from "@/lib/branding";

/**
 * Simplified, clickable end-to-end flow for Flexspace.
 * Five nodes · one linear journey · every block routes to the live page.
 *   Persona → Catalog (with manager approval built-in) → Marketplace → Unified Device Portal → MyPC
 */

const STEPS = [
  {
    to: "/",
    testId: "arch-step-persona",
    icon: Users,
    label: "Persona",
    sub: "You sign in",
    detail: "Developer · Sales · Executive · Designer · Field Engineer",
    tone: "slate",
  },
  {
    to: "/catalogue",
    testId: "arch-step-catalog",
    icon: LayoutGrid,
    label: "Catalog",
    sub: "Pick your hardware",
    detail: "Windows · Mac · Accessories · AI Relevance Score per device",
    tone: "blue",
    subsection: {
      testId: "arch-step-catalog-approval",
      icon: Gavel,
      label: "Manager approval",
      desc: "Dynamic per persona — some devices are pre-approved for you, others route to your manager via real email",
    },
  },
  {
    to: "/ai-marketplace",
    testId: "arch-step-marketplace",
    icon: Store,
    label: "Marketplace",
    sub: "Add AI apps & models",
    detail: "Persona-validated apps · on-device models · learning resources",
    tone: "indigoLight",
  },
  {
    to: "/integrations",
    testId: "arch-step-delivery",
    icon: Layers,
    label: "Unified Device Portal",
    sub: "Order · Fulfilment · Tracking",
    detail: null, // custom render — shows integration chips
    tone: "green",
    integrations: [
      { key: "servicenow", label: "ServiceNow" },
      { key: "intune",     label: "Intune" },
      { key: "bigfix",     label: "BigFix" },
      { key: "nexthink",   label: "Nexthink" },
    ],
  },
  {
    to: "/aipc",
    testId: "arch-step-mypc",
    icon: Cpu,
    label: "MyPC",
    sub: "Runs on your device",
    detail: null, // custom render — shows bullet list
    tone: "indigoDark",
    bullets: [
      { icon: Sparkles, text: "AI apps installed" },
      { icon: Activity, text: "Device UX score · trend" },
      { icon: Cog,      text: "Copilot+ readiness" },
      { icon: Ticket,   text: "Quick ticket view" },
    ],
  },
];

const TONE = {
  slate:       "bg-slate-50 border-slate-300 text-slate-800 hover:bg-slate-100",
  blue:        "bg-[#0033A0] border-[#0033A0] text-white hover:bg-[#002573]",
  indigoLight: "bg-[#EEF2FF] border-[#0033A0]/40 text-[#0033A0] hover:bg-[#0033A0]/10",
  amber:       "bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100",
  green:       "bg-[#293e40] border-[#293e40] text-white hover:bg-[#1a2828]",
  indigoDark:  "bg-[#0F172A] border-[#0F172A] text-white hover:bg-[#1e293b]",
};

// Chip styling contrasts with each block's background
const CHIP_TONE = {
  green: "bg-white/10 text-white border-white/25",
};

export default function ArchitectureFlow() {
  const { brand } = useBranding();
  return (
    <Card className="p-6 border-slate-200 shadow-sm" data-testid="architecture-flow">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display font-bold text-xl text-slate-900">
            E2E Amazon-like ordering experience — from wishlist to your desk
          </h2>
          <p className="text-xs text-slate-600 mt-1 max-w-3xl">
            One persona, one catalog, one portal — every hop is a real {brand.orgName}-approved service. Manager approval is built into the catalog step; tracking is built into fulfilment. Click any block to jump straight there.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-stretch">
        {STEPS.map((s, i) => (
          <div key={s.label} className="flex items-stretch">
            <Link
              to={s.to}
              data-testid={s.testId}
              className={`flex-1 rounded-md border-2 p-4 transition-colors ${TONE[s.tone]} focus:outline-none focus:ring-2 focus:ring-[#0033A0]/40`}
            >
              <div className="flex items-center gap-2">
                <s.icon size={16} />
                <span className="font-display font-black text-[15px] tracking-tight leading-tight">{s.label}</span>
              </div>
              <div className="text-[11px] font-semibold mt-1 opacity-90">{s.sub}</div>

              {s.detail && (
                <div className="text-[10px] mt-1.5 opacity-75 leading-snug">{s.detail}</div>
              )}

              {s.subsection && (
                <div
                  data-testid={s.subsection.testId}
                  className="mt-2 rounded border border-white/25 bg-white/15 p-1.5"
                >
                  <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest">
                    <s.subsection.icon size={10} />
                    {s.subsection.label}
                  </div>
                  <div className="text-[9px] opacity-90 mt-0.5 leading-snug">
                    {s.subsection.desc}
                  </div>
                </div>
              )}

              {s.integrations && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid={`${s.testId}-integrations`}>
                  {s.integrations.map((chip) => (
                    <span
                      key={chip.key}
                      data-testid={`${s.testId}-chip-${chip.key}`}
                      className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${CHIP_TONE[s.tone] || "bg-white/10 text-white border-white/25"}`}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              )}

              {s.bullets && (
                <ul className="mt-2 space-y-1" data-testid={`${s.testId}-bullets`}>
                  {s.bullets.map((b) => (
                    <li key={b.text} className="flex items-center gap-1.5 text-[10px] opacity-90">
                      <b.icon size={10} />
                      <span>{b.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Link>
            {i < STEPS.length - 1 && (
              <div className="hidden md:flex items-center justify-center px-1 text-slate-300">
                <ChevronRight size={14} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 flex-wrap text-[10px] text-slate-500">
        <Badge variant="outline" className="text-[10px] bg-white border-slate-200">Live: ServiceNow · Resend · Copilot</Badge>
        <Badge variant="outline" className="text-[10px] bg-white border-slate-200">Runtime: Intel OpenVINO · Nexthink Device UX</Badge>
        <Badge variant="outline" className="text-[10px] bg-white border-slate-200">Governance: BigFix · Intune · SAP Ariba</Badge>
      </div>
    </Card>
  );
}
